<script setup>
import { ref, computed, watch } from 'vue';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
import { useAttachmentLoadState } from 'dashboard/composables/useAttachmentLoadState';

const emit = defineEmits(['error']);
const hasError = ref(false);
const isLoaded = ref(false);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments, createdAt } =
  useMessageContext();
const { hasFailed, markLoadFailure, reset } = useAttachmentLoadState(createdAt);

const handleError = () => {
  hasError.value = true;
  isLoaded.value = false;
  markLoadFailure();
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

// The file may not be in the storage yet when the message is first received. message.updated is
// sent once the file is uploaded: remount the player, since the same src is not requested again.
const playerKey = ref(0);
watch(attachment, () => {
  if (!hasError.value) return;

  reset();
  hasError.value = false;
  playerKey.value += 1;
});

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <div class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <div
        v-if="hasFailed"
        class="flex items-center gap-1 text-center rounded-lg"
      >
        <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
        <p class="mb-0 text-n-slate-11">
          {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
        </p>
      </div>
      <video
        v-else
        :key="playerKey"
        controls
        class="rounded-lg skip-context-menu"
        :src="attachment.dataUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @loadedmetadata="isLoaded = true"
        @error="handleError"
      />
      <div
        v-if="!isLoaded && !hasFailed"
        data-testid="media-loading"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-1 pointer-events-none"
        :title="$t('COMPONENTS.MEDIA.LOADING')"
      >
        <Icon
          icon="i-lucide-loader-circle"
          class="size-6 text-n-slate-11 animate-spin"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="onError"
    @close="() => (showGallery = false)"
  />
</template>
